// Sidebar — left-rail navigation for authenticated users. Shows view
// items based on role (admin/lawyer/legalops/etc.) plus the
// notifications bell, current user info, and sign-out button.

import { useEffect, useMemo, useState } from "react";
import { T } from "../theme";
import type { UserRow } from "../types/api";
import { Btn } from "./primitives";
import { ClientPicker } from "./ClientPicker";
import { OrgBadge } from "./OrgBadge";
import { useClientScope } from "../contexts/ClientScopeContext";
import {
  documentIntelligencePath,
  navigateDocumentIntelligence,
} from "./docint/redesign/shared/router";
import { documentIntelligenceApi } from "./docint/redesign/shared/api";
import type { DocumentIntelligenceHistoryItem, DocumentIntelligenceRoute } from "./docint/redesign/shared/types";

const documentIntelligenceChildren: Array<{
  label: string;
  icon: string;
  route: DocumentIntelligenceRoute;
}> = [
  { label: "Assistant", icon: "◫", route: { screen: "assistant" } },
  { label: "Draft", icon: "▤", route: { screen: "draft" } },
  { label: "Review table", icon: "▦", route: { screen: "table" } },
  { label: "Playbook", icon: "▥", route: { screen: "playbooks" } },
  { label: "Workflows", icon: "⌘", route: { screen: "workflows" } },
  { label: "Project", icon: "□", route: { screen: "projects" } },
  { label: "Sources", icon: "▣", route: { screen: "sources" } },
  { label: "History", icon: "◷", route: { screen: "history" } },
];

export const Sidebar = ({ view = '', onNav = undefined, user = null, onLogout = undefined, notifCount = 0 }: { view?: string; onNav?: any; user?: UserRow | null; onLogout?: any; notifCount?: number }) => {
  const scope = useClientScope();
  const docintStorageKey = useMemo(
    () => `kontractai:document-intelligence-nav:${user?.id || user?.email || "current-user"}`,
    [user?.email, user?.id]
  );
  const [docintExpanded, setDocintExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem(docintStorageKey);
      return saved === null ? true : saved === "expanded";
    } catch {
      return true;
    }
  });
  const [docintPathname, setDocintPathname] = useState(window.location.pathname);
  const [docintActivities, setDocintActivities] = useState<DocumentIntelligenceHistoryItem[]>([]);
  const docintLastRouteKey = `${docintStorageKey}:last-route`;

  useEffect(() => {
    try {
      localStorage.setItem(docintStorageKey, docintExpanded ? "expanded" : "collapsed");
    } catch { /* local persistence is best-effort */ }
  }, [docintExpanded, docintStorageKey]);

  useEffect(() => {
    const syncPath = () => {
      const location = `${window.location.pathname}${window.location.search}`;
      setDocintPathname(location);
      if (window.location.pathname.startsWith("/document-intelligence")) {
        try { localStorage.setItem(docintLastRouteKey, location); } catch { /* best-effort */ }
      }
      void documentIntelligenceApi.history().then(setDocintActivities).catch(() => undefined);
    };
    syncPath();
    window.addEventListener("popstate", syncPath);
    return () => window.removeEventListener("popstate", syncPath);
  }, [docintLastRouteKey]);
  // Sidebar items are gated by nav_permissions from the auth response
  // (RBAC Phase 1). Legacy fallback: if nav_permissions is missing — e.g.
  // the user hasn't re-authed since the backend migration, or role_id is
  // still null — fall back to the role-based visibility the site has used
  // historically. Removing the fallback is safe once all active sessions
  // have re-authed; leaving it in during rollout avoids locking anyone out.
  const nav = user?.nav_permissions;
  const role = user?.role || '';
  const see = (key: string, legacyRoles: string[] | null = null): boolean => {
    if (nav && typeof nav === 'object' && Object.prototype.hasOwnProperty.call(nav, key)) return !!nav[key];
    if (legacyRoles === null) return true; // item always visible
    return legacyRoles.includes(role);
  };
  const items = [
    ...(see('dashboard', ['admin','lawyer','legalops','commercial_ops','contract_manager','requester','executive']) ? [{ id: "dashboard", label: "Dashboard", icon: "▣" }] : []),
    ...(see('requests', null) ? [{ id: "requests", label: "Requests", icon: "◫" }] : []),
    ...(see('self_service', ["requester","admin","legalops","commercial_ops","lawyer","contract_manager"]) ? [{ id: "selfservice", label: "Self-Service", icon: "◈" }] : []),
    ...(see('drafting', ["admin","legalops","lawyer","contract_manager","commercial_ops"]) ? [{ id: "drafting", label: "Drafting", icon: "✏" }] : []),
    ...(see('my_queue', ["admin","legalops","lawyer","contract_manager","commercial_ops","requester"]) ? [{ id: "queue", label: "My Queue", icon: "◧" }] : []),
    // My Desk — in-house edition only, lawyer-role only. Sidebar gate
    // mirrors the backend gate (/api/dashboard/my-desk returns 404 outside
    // these conditions). Backend gate is authoritative.
    ...((scope?.edition === 'in_house' && role === 'lawyer') ? [{ id: "mydesk", label: "My Desk", icon: "▤" }] : []),
    ...(see('repository', ["admin","legalops","commercial_ops","lawyer","executive"]) ? [{ id: "repo", label: "Repository", icon: "◨" }] : []),
    ...(see('ai_import', ["admin","legalops","commercial_ops"]) ? [{ id: "import", label: "AI Import", icon: "◬" }] : []),
    ...(see('docint', ["admin","legalops","commercial_ops","lawyer","contract_manager"]) ? [{ id: "docint", label: "Document Intelligence", icon: "*" }] : []),
    ...(see('search', ['admin','lawyer','legalops','commercial_ops','contract_manager','requester','executive']) ? [{ id: "search", label: "Search", icon: "⌕" }] : []),
    ...(see('ai_chat', ['admin','lawyer','legalops','commercial_ops','contract_manager','requester','executive']) ? [{ id: "aichat", label: "AI Chat", icon: "◆" }] : []),
    ...(see('execution_queue', ["admin","legalops","commercial_ops"]) ? [{ id: "execqueue", label: "Execution Queue", icon: "✍" }] : []),
    ...(see('reports', ["admin","legalops","commercial_ops","lawyer","contract_manager","executive"]) ? [{ id: "reports", label: "Reports", icon: "📊" }] : []),
    ...(see('administration', ["admin"]) ? [{ id: "admin", label: "Administration", icon: "◉" }] : []),
    // Platform Console — FULL operator only (admin in the bootstrap tenant, walls-up).
    // Shown only when the auth payload flags is_operator; the server-side gate
    // (routes/platformAdmin.js) is authoritative regardless.
    //
    // The walls-down in-house AI-routing operator (is_ai_routing_operator, an
    // admin.manage_ai_routing holder) no longer gets a separate sidebar item — the
    // operator AI-routing console is now consolidated into Administration ▸ AI &
    // Models ▸ AI Routing (AiRoutingAdmin), which they already reach as an admin.
    // One discoverable surface; no widening (the AdminView render is gated on the
    // same is_operator||is_ai_routing_operator flag, and /platform/* stays operatorOnly).
    // CUTOVER (owner decision): ONE operator entry. The redesigned console replaces
    // the old "Platform" console and absorbs "Setup Wizard" — both were separate
    // sidebar items while the new console was being evaluated, which left two
    // customer-creation paths side by side calling different endpoints. The console's
    // own wizard is the one that runs collision classification and issues the admin
    // invite, so it is the survivor. Same is_operator gate; the server-side
    // /api/platform gate is authoritative regardless.
    ...(user?.is_operator ? [{ id: "console", label: "Console", icon: "◧" }] : []),
    { id: "help", label: "User Guide", icon: "?" },
  ];
  // OPERATOR SCOPE: the platform operator's console presents ONLY operator tasks.
  // Tenant-product nav is removed for operators (the backend also denies those
  // routes in the operator context, so this is UI scoping atop a genuine access gate).
  const visibleItems = user?.is_operator
    ? items.filter((i: any) => i.id === "console" || i.id === "help")
    : items;
  return (
    <div className="kai-sidebar" style={{ width: 280, minHeight: "100vh", backgroundColor: "#FFFFFF", borderRight: `1px solid ${T.brd}`, display: "flex", flexDirection: "column", flexShrink: 0 }}>
      <div className="kai-sidebar-logo" style={{ padding: "24px 20px 20px", borderBottom: `1px solid ${T.brd}` }}>
        <img src="/logo.png" alt="KontractAI" style={{ height: 60, width: "auto", objectFit: "contain" }} />
      </div>
      <nav style={{ flex: 1, padding: "12px 8px" }}>
        {visibleItems.map((i: any) => {
          if (i.id !== "docint") {
            return (
              <button className="kai-nav-button" title={i.label} aria-label={i.label} key={i.id} onClick={() => onNav?.(i.id)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "11px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 15, fontWeight: view === i.id ? 600 : 400, color: view === i.id ? T.tx : T.txM, backgroundColor: view === i.id ? T.accS : "transparent", textAlign: "left", fontFamily: "inherit", marginBottom: 2 }}>
                <span style={{ fontSize: 15, opacity: .7 }}>{i.icon}</span><span className="kai-nav-label">{i.label}</span>
              </button>
            );
          }

          return (
            <div key={i.id} className="kai-docint-nav-tree">
              <button
                className="kai-nav-button"
                title={i.label}
                aria-label={i.label}
                aria-expanded={docintExpanded}
                onClick={() => {
                  if (view !== "docint") {
                    setDocintExpanded(true);
                    let restored = "";
                    try { restored = localStorage.getItem(docintLastRouteKey) || ""; } catch { /* best-effort */ }
                    if (restored.startsWith("/document-intelligence/")) {
                      window.history.pushState({ docint: true }, "", restored);
                      window.dispatchEvent(new PopStateEvent("popstate"));
                    } else {
                      navigateDocumentIntelligence({ screen: "assistant" });
                    }
                    onNav?.("docint");
                  } else {
                    setDocintExpanded((expanded) => !expanded);
                  }
                }}
                style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "11px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 15, fontWeight: view === i.id ? 600 : 400, color: view === i.id ? T.tx : T.txM, backgroundColor: view === i.id && docintExpanded ? T.accS : "transparent", textAlign: "left", fontFamily: "inherit", marginBottom: 2 }}
              >
                <span style={{ fontSize: 15, opacity: .7 }}>{i.icon}</span>
                <span className="kai-nav-label" style={{ flex: 1 }}>{i.label}</span>
                <span className="kai-nav-label" aria-hidden="true" style={{ fontSize: 12 }}>
                  {docintExpanded ? "▾" : "▸"}
                </span>
              </button>

              {docintExpanded ? (
                <div
                  className="kai-docint-nav-children"
                  style={{
                    display: "grid",
                    gap: 1,
                    padding: "2px 0 6px 20px",
                    borderLeft: "1px solid rgba(32,30,29,.18)",
                    marginLeft: 20,
                  }}
                >
                  {documentIntelligenceChildren.map((child) => {
                    const path = documentIntelligencePath(child.route);
                    const activePathname = docintPathname.split("?")[0];
                    const active = view === "docint"
                      && (child.route.screen === "assistant"
                        ? activePathname === path || activePathname === "/document-intelligence"
                        : activePathname.startsWith(path));
                    return (
                      <button
                        key={child.label}
                        type="button"
                        aria-current={active ? "page" : undefined}
                        onClick={() => {
                          navigateDocumentIntelligence(child.route);
                          onNav?.(child.route.screen === "assistant" ? "docint:new-assistant" : "docint");
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          width: "100%",
                          minHeight: 34,
                          padding: "7px 8px",
                          border: 0,
                          borderRadius: 0,
                          background: active ? "#fff2ef" : "transparent",
                          color: active ? "#ec3013" : T.txM,
                          cursor: "pointer",
                          textAlign: "left",
                          fontFamily: "inherit",
                          fontSize: 13,
                          fontWeight: active ? 700 : 400,
                        }}
                      >
                        <span aria-hidden="true" style={{ width: 15, opacity: .78 }}>{child.icon}</span>
                        <span className="kai-nav-label">{child.label}</span>
                      </button>
                    );
                  })}
                  <div className="kai-nav-label" style={{ padding: "10px 8px 4px", fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: T.txD }}>
                    Active threads
                  </div>
                  {docintActivities.slice(0, 5).map((activity) => (
                    <button
                      key={`${activity.type}:${activity.id}`}
                      type="button"
                      className="kai-nav-label"
                      title={activity.title}
                      onClick={() => {
                        const screen = activity.type === "review_table" ? "table" : activity.type === "redline" ? "draft" : activity.type === "workflow" ? "workflow-run" : "thread";
                        navigateDocumentIntelligence({ screen, id: activity.id });
                        onNav?.("docint");
                      }}
                      style={{ border: 0, background: "transparent", color: T.txM, cursor: "pointer", fontFamily: "inherit", fontSize: 11, overflow: "hidden", padding: "5px 8px", textAlign: "left", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}
                    >
                      {activity.title}
                    </button>
                  ))}
                  {!docintActivities.length && <div className="kai-nav-label" style={{ padding: "4px 8px 8px", fontSize: 11, color: T.txD }}>No saved work yet.</div>}
                  <button
                    type="button"
                    aria-current={view === "docint" && docintPathname.startsWith("/document-intelligence/settings") ? "page" : undefined}
                    onClick={() => {
                      navigateDocumentIntelligence({ screen: "settings" });
                      onNav?.("docint");
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      width: "100%",
                      minHeight: 34,
                      padding: "7px 8px",
                      border: 0,
                      borderTop: "1px solid rgba(32,30,29,.18)",
                      borderRadius: 0,
                      background: docintPathname.startsWith("/document-intelligence/settings") ? "#fff2ef" : "transparent",
                      color: docintPathname.startsWith("/document-intelligence/settings") ? "#ec3013" : T.txM,
                      cursor: "pointer",
                      textAlign: "left",
                      fontFamily: "inherit",
                      fontSize: 13,
                    }}
                  >
                    <span aria-hidden="true" style={{ width: 15 }}>⚙</span>
                    <span className="kai-nav-label">Settings</span>
                  </button>
                </div>
              ) : null}
            </div>
          );
        })}
      </nav>
      <button className="kai-nav-button" title="Notifications" aria-label="Notifications" onClick={() => onNav?.("notifications")} style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 8px 4px", padding: "11px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 15, color: T.txM, backgroundColor: view === "notifications" ? T.accS : "transparent", textAlign: "left", fontFamily: "inherit" }}>
        <span>🔔</span> <span className="kai-nav-label">Notifications</span>
        {notifCount > 0 && <span style={{ marginLeft: "auto", fontSize: 11, padding: "2px 8px", borderRadius: 8, backgroundColor: T.red, color: "#fff", fontWeight: 700 }}>{notifCount}</span>}
      </button>
      <div className="kai-sidebar-footer" style={{ padding: "14px 18px", borderTop: `1px solid ${T.brd}` }}>
        {!user?.is_operator && <ClientPicker />}
        <div style={{ fontSize: 14, color: T.tx, fontWeight: 600 }}>{user?.name}</div>
        <div style={{ fontSize: 12, color: T.txD, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ textTransform: "capitalize" }}>{user?.role?.replace("legalops", "Legal Ops").replace("commercial_ops", "Commercial Ops").replace("contract_manager", "Contract Manager").replace("business_reviewer", "Business Reviewer")}</span>
          <OrgBadge org_type={scope.organization?.org_type} size="xs" />
        </div>
        <Btn v="ghost" sz="sm" onClick={onLogout}>Sign Out</Btn>
      </div>
    </div>
  );
};
